/**
 * Left click on an IFC element selects it and opens the Properties tab; hover
 * tints it. Like every other map interaction here it owns its own
 * ScreenSpaceEventHandler and decides from the pick result whether the click is
 * its business. Silent while a tool (measure / clipping / upload), damage drawing
 * or tileset location editing is active.
 */
import {
	Cesium3DTileFeature,
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
	defined,
} from 'cesium';
import type { Cartesian2 } from 'cesium';
import { useEffect, useMemo, useRef } from 'react';
import { useCesium } from '@/context/CesiumContext';
import { useIfcFeatures } from '@/context/IfcFeatureContext';
import { useTilesetLocationEdit } from '@/context/TilesetLocationEditContext';
import { IFC_COLOR_DEFAULT, IFC_COLOR_HOVER, IFC_COLOR_SELECTED } from '@/helper/ifc/colors';

const HOVER_THROTTLE_MS = 50;
const SUMMARY_PROPERTIES = ['IfcClass', 'Name', 'GlobalId'] as const;

const tilesetKeyOf = (feature: Cesium3DTileFeature): string | undefined =>
	(feature.tileset as any)?.uniqueId as string | undefined;

const summaryOf = (feature: Cesium3DTileFeature): Record<string, string> => {
	const summary: Record<string, string> = {};
	for (const key of SUMMARY_PROPERTIES) {
		const value = feature.getProperty(key);
		if (value !== undefined && value !== null) summary[key] = String(value);
	}
	return summary;
};

export default function IfcPickHandler() {
	const { viewer, activeTool, drawMode } = useCesium();
	const { entries, selection, panel, select, clearSelection, closePanel, openPanel } =
		useIfcFeatures();
	const { editing } = useTilesetLocationEdit();

	const blocked =
		activeTool !== 'none' || drawMode === 'EDIT' || drawMode === 'INSERT' || editing !== null;

	// Refs so the Cesium handlers (registered once per viewer) always see the latest values.
	const blockedRef = useRef(blocked);
	blockedRef.current = blocked;
	const keys = useMemo(() => new Set(Object.keys(entries)), [entries]);
	const keysRef = useRef(keys);
	keysRef.current = keys;
	const selectionRef = useRef(selection);
	selectionRef.current = selection;
	const panelRef = useRef(panel);
	panelRef.current = panel;
	const hovered = useRef<Cesium3DTileFeature | null>(null);

	useEffect(() => {
		if (!viewer) return;
		const scene = viewer.scene;
		const canvas = scene.canvas;
		const handler = new ScreenSpaceEventHandler(canvas);

		const isIfcFeature = (picked: unknown): picked is Cesium3DTileFeature =>
			picked instanceof Cesium3DTileFeature && keysRef.current.has(tilesetKeyOf(picked) ?? '');

		const isSelected = (feature: Cesium3DTileFeature) => {
			const current = selectionRef.current;
			return (
				!!current &&
				current.key === tilesetKeyOf(feature) &&
				current.featureId === feature.featureId
			);
		};

		const clearHover = () => {
			const previous = hovered.current;
			if (!previous) return;
			try {
				previous.color = isSelected(previous) ? IFC_COLOR_SELECTED : IFC_COLOR_DEFAULT;
			} catch {
				// The content that owned the feature was unloaded meanwhile: nothing to restore.
			}
			hovered.current = null;
			canvas.style.cursor = '';
			scene.requestRender();
		};

		handler.setInputAction((movement: { position: Cartesian2 }) => {
			if (blockedRef.current) return;
			const picked = scene.pick(movement.position);

			if (isIfcFeature(picked)) {
				const key = tilesetKeyOf(picked) as string;
				clearHover();
				select({ key, featureId: picked.featureId, summary: summaryOf(picked) });
				openPanel(key, 'properties');
				return;
			}

			// An entity (damage, sensor, panorama trigger): its own handler takes the slot.
			if (defined(picked) && picked.id) {
				clearSelection();
				closePanel();
				return;
			}

			// Empty space or a non-IFC tileset.
			clearSelection();
			if (panelRef.current?.tab === 'properties') closePanel();
		}, ScreenSpaceEventType.LEFT_CLICK);

		let lastHoverAt = 0;
		handler.setInputAction((movement: { endPosition: Cartesian2 }) => {
			if (blockedRef.current || keysRef.current.size === 0) {
				clearHover();
				return;
			}
			const now = performance.now();
			if (now - lastHoverAt < HOVER_THROTTLE_MS) return;
			lastHoverAt = now;

			const picked = scene.pick(movement.endPosition);
			const feature = isIfcFeature(picked) ? picked : null;
			if (feature === hovered.current) return;

			clearHover();
			if (!feature) return;
			hovered.current = feature;
			if (!isSelected(feature)) feature.color = IFC_COLOR_HOVER;
			canvas.style.cursor = 'pointer';
			scene.requestRender();
		}, ScreenSpaceEventType.MOUSE_MOVE);

		// Cesium's MOUSE_MOVE stops at the canvas edge; a native leave event clears the hover.
		const onCanvasLeave = () => clearHover();
		canvas.addEventListener('mouseleave', onCanvasLeave);

		return () => {
			canvas.removeEventListener('mouseleave', onCanvasLeave);
			clearHover();
			handler.destroy();
		};
	}, [viewer, select, clearSelection, closePanel, openPanel]);

	return null;
}
